import { useState, useRef, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import SimpleTable from '../../SimpleTable';
import { useFormikContext } from '../../Formik/forms';
import { useNotifications } from "lib/NotificationsProvider";
import { gql } from "generated/gql"
import { useMutation, useSuspenseQuery } from "@apollo/client"
import { UPDATE_ORGANIZATION_MUTATION } from "../UserTable";
import FormWrapper from "components/Formik/FormWrapper"
import * as yup from 'yup'

import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditIcon from '@mui/icons-material/Edit';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import PeopleIcon from '@mui/icons-material/People';
import { Typography } from "@mui/material"
import MainContainer from "components/layout/MainContainer/MainContainer";
import Checkbox from '@mui/material/Checkbox'
import TextField from '@mui/material/TextField'
import FormControlLabel from '@mui/material/FormControlLabel'
import StandardDialog from "components/UI/Dialog/StandardDialog";
import Stack from "@mui/material/Stack";
import { CreateOrganizationMutationVariables, OrganizationFieldsFragment, UpdateOrganizationMutationVariables } from "generated/graphql";
import { Column } from "components/SimpleTable/types";

type RowType = OrganizationFieldsFragment

const COLUMNS: Column<RowType>[] = [
  { title: 'ID', field: 'id', width: '0px', visible: true },
  { title: 'Name', field: 'name', visible: true },
]

const GET_ORGANIZATIONS_QUERY = gql(`
  query GetOrganizations {
    organizations {
      id
      name
      permitAiAnalysis
      permitPredictedValue
    }
  }
`)

export const CREATE_ORGANIZATION_MUTATION = gql(`
  mutation CreateOrganization(
    $name: String!
    $permitAiAnalysis: Boolean
    $permitPredictedValue: Boolean
  ) {
    mutationResult:createOrganization (input: {
      name: $name
      permitAiAnalysis: $permitAiAnalysis
      permitPredictedValue: $permitPredictedValue
    }) {
      id
      errors
      organization {
        ...OrganizationFields
      }
    }
  }
`)

export const DESTROY_ORGANIZATION_MUTATION = gql(`
  mutation DestroyOrganization(
    $id: ID!,
  ) {
    destroyOrganization(input: {
      id: $id,
    }) {
      id
      errors
      organization {
        ...OrganizationFields
      }
    }
  }
`)

export const organizationSchema = yup.object().shape({
  id: yup.string().nullable(),
  name: yup.string().required('Name is required').typeError('Name is required'),
  permitAiAnalysis: yup.boolean().nullable(),
  permitPredictedValue: yup.boolean().nullable(),
  scimEnabled: yup.boolean().nullable(),
  sessionTimeoutMinutes: yup.number().nullable(),
})

type Organization = {
  id?: string
  name: string
  permitAiAnalysis: boolean
  permitPredictedValue: boolean
  scimEnabled: boolean
  sessionTimeoutMinutes: number
}

const AddOrganizationModal = function({onClose}) {
  const { values, setValues, submitForm, isSubmitting, dirty } = useFormikContext<Organization>()

  const handleCheckboxPermitAIClick = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setValues(values => ({
      ...values,
      permitAiAnalysis: event.target.checked
    }))
  }, [setValues])

  const handleCheckboxProjectedValueClick = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setValues(values => ({
      ...values,
      permitPredictedValue: event.target.checked
    }))
  }, [setValues])

  const handleChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setValues(values => ({
      ...values,
      name: event.target.value
    }))
  }, [setValues])

  return (
    <StandardDialog
      title={`${values.id ? 'Edit' : 'Add'} organization`}
      onCancel={onClose}
      onConfirm={submitForm}
      disabled={isSubmitting || !dirty}
      confirmText='Save'
      maxWidth='400px'
    >
      <Stack direction='column' sx={{ mr: 7 }} gap={2}>
        <TextField
          name={"name"}
          label="Name"
          value={values.name || ''}
          placeholder="Name"
          variant="outlined"
          onChange={handleChange}
        />
        <Typography variant="h2" sx={{ marginTop: '15px' }}>Premium Features</Typography>
        <FormControlLabel
          control={<Checkbox sx={{padding: '2px 4px 2px 9px'}} checked={values.permitAiAnalysis} name="permitAiAnalysis" onChange={handleCheckboxPermitAIClick} />}
          label="Permit AI Analysis"
        />
        <FormControlLabel
          control={<Checkbox sx={{padding: '2px 4px 2px 9px'}} checked={values.permitPredictedValue} onChange={handleCheckboxProjectedValueClick} />}
          label="Permit Predicted Value"
        />
      </Stack>
    </StandardDialog>
  )
}

export default function OrganizationTable() {
  const { addNotification } = useNotifications()
  const [organizationForModal, setOrganizationForModal] = useState<UpdateOrganizationMutationVariables | CreateOrganizationMutationVariables |  null>(null)
  const dataManagerRef = useRef(null);
  const navigate = useNavigate();
  const [refreshData, _setRefreshData] = useState(0)
  const { data: { organizations }, refetch } = useSuspenseQuery(GET_ORGANIZATIONS_QUERY)
  const [gqlUpdate] = useMutation(UPDATE_ORGANIZATION_MUTATION)
  const [gqlCreate] = useMutation(CREATE_ORGANIZATION_MUTATION)
  const [gqlDestroy] = useMutation(DESTROY_ORGANIZATION_MUTATION)

  useEffect(() => {
    refetch()
  }, [refetch, refreshData])

  const handleEdit = (_event: unknown, rowData: RowType) => {
    setOrganizationForModal({ id: rowData.id, name: rowData.name, permitAiAnalysis: rowData.permitAiAnalysis, permitPredictedValue: rowData.permitPredictedValue });
  }

  const handleManage = (_event: unknown, rowData: RowType) => {
    navigate(`${rowData.id}/users`)
  }

  const handleAdd = (_event: unknown) => {
    setOrganizationForModal({ name: '', permitAiAnalysis: false, permitPredictedValue: false })
  };

  const closeModal = () => {
    setOrganizationForModal(null)
  }

  const createOrUpdateOrganization = useCallback((values: CreateOrganizationMutationVariables | UpdateOrganizationMutationVariables, { setSubmitting }) => {
    setSubmitting(true)
    const isEditing = 'id' in values

    const promise = isEditing ? gqlUpdate({ variables: values }) : gqlCreate({ variables: values })

    promise.then((_result: unknown) => {
      addNotification({ variant: 'notice', message: `${isEditing ? 'Updat' : 'Creat'}ed ${values.name}` })
      closeModal()
    }).catch(_error => {
      addNotification({ variant: 'alert', message: `Trouble ${isEditing ? 'updat' : 'creat'}ing organization. Please try again later.` })
    }).finally(() => {
      setSubmitting(false)
      refetch()
    })
  }, [gqlUpdate, gqlCreate, addNotification, refetch])


  const handleDelete = (_event: unknown, rowData: RowType) => {
    if(!confirm(`Are you sure you want to delete the organization ${rowData['name']}?`)) {
      return;
    }

    const variables = { id: rowData['id'] }

    gqlDestroy({ variables: variables }).then(() => {
      addNotification({variant: 'notice', message: `Deleted organization ${rowData['name']}.`});
      refetch()
    }).catch(_error => {
      addNotification({variant: 'alert', message: 'Failed to delete organization.'});
    })
  }

  return (
    <MainContainer>
      <div style={{ maxWidth: "100%" }}>
        <SimpleTable
          dataManagerRef={dataManagerRef}
          columns={COLUMNS}
          data={organizations}
          options={{
            columnsButton: true,
            sortColumn: 'name',
            sortOrder: 1,
          }}
          title="Organizations"
          rowActions={[
            {
              icon: () => <DeleteOutlineIcon/>,
              tooltip: 'Delete Organization',
              onClick: handleDelete,
            },
            {
              icon: () => <EditIcon/>,
              tooltip: 'Edit organization',
              onClick: handleEdit,
            },
            {
              icon: () => <PeopleIcon/>,
              tooltip: 'Manage organization members',
              onClick: handleManage,
            },
          ]}
          actions={[
            {
              icon: () => <AddCircleIcon/>,
              tooltip: 'Add new organization',
              onClick: handleAdd,
            },
          ]}
        />
      </div>
      {organizationForModal && (
        <FormWrapper data={organizationForModal} schema={organizationSchema} onSubmit={createOrUpdateOrganization}>
          <AddOrganizationModal
            onClose={closeModal}
          />
        </FormWrapper>
      )}
    </MainContainer>
  )
}
